import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { ScrollArea } from "./ui/scroll-area";

const Section = ({ n, title, children }) => (
  <section className="space-y-2">
    <h3 className="text-white font-display text-[15px] tracking-tight flex items-baseline gap-2">
      <span className="text-teal font-mono text-[11px] tracking-[0.18em]">{n}</span>
      <span>{title}</span>
    </h3>
    <div className="text-sm text-white/70 leading-relaxed">{children}</div>
  </section>
);

export function TosModal({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tos-modal" className="max-w-2xl bg-charcoal border-border">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-white">SaiResponse — Terms of Service</DialogTitle>
          <DialogDescription className="text-white/50 font-mono text-[11px] tracking-[0.14em] uppercase">
            Last Updated: May 21, 2026
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-5 mt-1">
            <Section n="1." title="ACCEPTANCE OF TERMS">
              By creating an account or accessing the SaiResponse platform ("Service"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.
            </Section>

            <Section n="2." title="SERVICE DESCRIPTION & MULTI-TENANCY PRIVACY">
              SaiResponse provides an automated AI-assisted communication tool utilizing decentralized LLM APIs. The Service is built on a strict multi-tenant architecture designed to programmatically isolate user data. Under no circumstances will one user have visibility into another user's account data, drafts, or configuration settings.
            </Section>

            <Section n="3." title="FREE TRIAL & SUBSCRIPTION FUNNEL">
              <ul className="list-disc pl-5 space-y-1.5 mt-1">
                <li>The service offers a 3-day frictionless free trial upon Google SSO registration without requiring a credit card.</li>
                <li>Following the trial, access enters a restricted notification period for Days 4–6, before a non-dismissible subscription hard-lock occurs on Day 7.</li>
                <li>Subscriptions are billed on a recurring monthly ($29) or annual ($240) basis via Stripe. Failed or bounced recurring payments will result in instant automated account suspension.</li>
              </ul>
            </Section>

            <Section n="4." title="FAIR-USE & ABUSE PREVENTION">
              To protect server integrity and API infrastructure, paid accounts are bound to a strict fair-use cap of 500 AI generations per billing cycle. System abuse, automated account creation using disposable email domains, or scanning infrastructure anomalies will result in immediate termination by the system administrator without a refund.
            </Section>

            <Section n="5." title="INTELLECTUAL PROPERTY & LIABILITY LIMITATIONS">
              You retain all intellectual property rights over the text inputs you paste into the workspace. SaiResponse handles data text generation ephemerally and is not liable for errors, hallucinations, accuracy slip-ups, or legal fallout arising from communications you send using drafts generated by the Service.
            </Section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export function PrivacyModal({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="privacy-modal" className="max-w-2xl bg-charcoal border-border">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-white">SaiResponse — Privacy Policy</DialogTitle>
          <DialogDescription className="text-white/50 font-mono text-[11px] tracking-[0.14em] uppercase">
            Last Updated: May 21, 2026
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-5 mt-1">
            <Section n="1." title="DATA COLLECTION PRINCIPLES">
              We believe in absolute data minimization. We only collect the minimal infrastructure data required to safely run a subscription service:
              <ul className="list-disc pl-5 space-y-1.5 mt-2">
                <li>Google Account Metadata (User ID, validated Email address, full Name).</li>
                <li>Aggregated usage metrics (Timestamps, token volume consumed, count of generations) to enforce fair-use limits.</li>
              </ul>
            </Section>

            <Section n="2." title="EPHEMERAL DATA SHIELD (WHAT WE DO NOT COLLECT)">
              To guarantee enterprise-grade data privacy, SaiResponse <span className="text-teal">does not permanently cache, log, or store</span> the text of the emails, tickets, or internal Slack messages you paste into the system workspace, nor do we store the resulting AI outputs. All text inference happens completely in memory.
            </Section>

            <Section n="3." title="NO DATA TRAINING GUARANTEE">
              Your business communications are entirely confidential. Your inputs and outputs are never tracked, aggregated, sold, or used to train public or foundational language models.
            </Section>

            <Section n="4." title="THIRD-PARTY INFRASTRUCTURE">
              Payment processing is securely managed off-site by Stripe; we never store your credit card information. Automated transactional notification communications are delivered securely via Resend. System state records are contained within encrypted database instances running isolated tenant checks.
            </Section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
